/**
 * Resource archive hero carousel — stacked slides with prev/next peeks and transitions.
 */

const SWIPE_THRESHOLD_PX = 48;
const SWIPE_AXIS_RATIO = 1.25;

const SLIDE_FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]',
	'area[href]',
	'iframe',
	'object',
	'embed',
	'[contenteditable="true"]',
].join(', ');

/**
 * Keep keyboard focus out of non-active slides (peek + hidden).
 *
 * @param {Element} slide
 * @param {boolean} isActive
 */
function syncSlideFocusables(slide, isActive) {
	slide.querySelectorAll(SLIDE_FOCUSABLE_SELECTOR).forEach((el) => {
		if (isActive) {
			if (!el.hasAttribute('data-carousel-tabindex')) {
				return;
			}
			const previous = el.getAttribute('data-carousel-tabindex');
			el.removeAttribute('data-carousel-tabindex');
			if (previous === '') {
				el.removeAttribute('tabindex');
			} else {
				el.setAttribute('tabindex', previous);
			}
			return;
		}

		if (!el.hasAttribute('data-carousel-tabindex')) {
			el.setAttribute(
				'data-carousel-tabindex',
				el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : ''
			);
		}
		el.setAttribute('tabindex', '-1');
	});
}

/**
 * Bind horizontal swipe navigation without blocking vertical page scroll.
 *
 * @param {Element} element
 * @param {() => void} onSwipeLeft
 * @param {() => void} onSwipeRight
 */
function bindSwipeNavigation(element, onSwipeLeft, onSwipeRight) {
	let startX = 0;
	let startY = 0;
	let tracking = false;
	let swipeGesture = false;

	const reset = () => {
		tracking = false;
		swipeGesture = false;
	};

	const suppressClickAfterSwipe = () => {
		const preventClick = (event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
		};

		element.addEventListener('click', preventClick, { capture: true, once: true });
	};

	element.addEventListener('pointerdown', (event) => {
		if (event.pointerType === 'mouse' && event.button !== 0) {
			return;
		}

		tracking = true;
		swipeGesture = false;
		startX = event.clientX;
		startY = event.clientY;
	});

	element.addEventListener('pointermove', (event) => {
		if (!tracking) {
			return;
		}

		const deltaX = event.clientX - startX;
		const deltaY = event.clientY - startY;

		if (
			!swipeGesture &&
			Math.abs(deltaX) > 10 &&
			Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_AXIS_RATIO
		) {
			swipeGesture = true;

			if (typeof element.setPointerCapture === 'function') {
				element.setPointerCapture(event.pointerId);
			}
		}

		if (swipeGesture) {
			event.preventDefault();
		}
	});

	element.addEventListener('pointerup', (event) => {
		if (!tracking) {
			return;
		}

		const deltaX = event.clientX - startX;
		const deltaY = event.clientY - startY;
		const isHorizontalSwipe =
			Math.abs(deltaX) >= SWIPE_THRESHOLD_PX &&
			Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_AXIS_RATIO;

		if (isHorizontalSwipe) {
			if (deltaX < 0) {
				onSwipeLeft();
			} else {
				onSwipeRight();
			}

			suppressClickAfterSwipe();
		}

		if (typeof element.releasePointerCapture === 'function' && element.hasPointerCapture(event.pointerId)) {
			element.releasePointerCapture(event.pointerId);
		}

		reset();
	});

	element.addEventListener('pointercancel', reset);
}

export function initResourceArchiveHero() {
	const carousels = document.querySelectorAll('[data-resource-archive-carousel]');
	if (!carousels.length) {
		return;
	}

	carousels.forEach((carousel) => {
		const dots = carousel.querySelectorAll('.resource-archive-hero__carousel-dot');
		const slides = carousel.querySelectorAll('.resource-archive-hero__slide');
		const status = carousel.querySelector('[data-resource-archive-carousel-status]');
		const prevButton = carousel.querySelector('[data-resource-archive-carousel-prev]');
		const nextButton = carousel.querySelector('[data-resource-archive-carousel-next]');

		if (slides.length < 2) {
			return;
		}

		let activeIndex = Math.max(
			0,
			[...slides].findIndex((slide) => slide.classList.contains('is-active'))
		);
		let isAnimating = false;

		const applyPositions = () => {
			const total = slides.length;
			const prevIndex = (activeIndex - 1 + total) % total;
			const nextIndex = (activeIndex + 1) % total;

			slides.forEach((slide, slideIndex) => {
				slide.classList.remove('is-active', 'is-prev', 'is-next', 'is-hidden');

				let position = 'is-hidden';

				if (slideIndex === activeIndex) {
					position = 'is-active';
				} else if (total === 2) {
					position = activeIndex === 0 ? 'is-next' : 'is-prev';
				} else if (slideIndex === prevIndex) {
					position = 'is-prev';
				} else if (slideIndex === nextIndex) {
					position = 'is-next';
				}

				slide.classList.add(position);

				const isActive = position === 'is-active';
				const isVisible = position !== 'is-hidden';
				slide.toggleAttribute('hidden', !isVisible);
				slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
				syncSlideFocusables(slide, isActive);
			});

			dots.forEach((dot, dotIndex) => {
				dot.classList.toggle('is-active', dotIndex === activeIndex);
			});
		};

		const goTo = (index) => {
			if (isAnimating) {
				return;
			}

			const nextIndex = (index + slides.length) % slides.length;
			if (nextIndex === activeIndex) {
				return;
			}

			isAnimating = true;
			activeIndex = nextIndex;
			applyPositions();

			if (status) {
				status.textContent =
					slides[activeIndex].getAttribute('aria-label') ||
					`Slide ${activeIndex + 1} of ${slides.length}`;
			}

			window.setTimeout(() => {
				isAnimating = false;
			}, 450);
		};

		slides.forEach((slide) => {
			slide.addEventListener(
				'click',
				(event) => {
					if (slide.classList.contains('is-prev')) {
						event.preventDefault();
						event.stopPropagation();
						goTo(activeIndex - 1);
						return;
					}

					if (slide.classList.contains('is-next')) {
						event.preventDefault();
						event.stopPropagation();
						goTo(activeIndex + 1);
					}
				},
				true
			);
		});

		if (prevButton) {
			prevButton.addEventListener('click', () => {
				goTo(activeIndex - 1);
			});
		}

		if (nextButton) {
			nextButton.addEventListener('click', () => {
				goTo(activeIndex + 1);
			});
		}

		carousel.addEventListener('keydown', (event) => {
			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				goTo(activeIndex - 1);
			}

			if (event.key === 'ArrowRight') {
				event.preventDefault();
				goTo(activeIndex + 1);
			}
		});

		const swipeTarget = carousel.querySelector('.resource-archive-hero__deck') || carousel;
		bindSwipeNavigation(
			swipeTarget,
			() => goTo(activeIndex + 1),
			() => goTo(activeIndex - 1)
		);

		applyPositions();
	});
}
